<template>
    <div class="functions-tab">
        <div class="tab-header">
            <h3>Fonksiyonlar</h3>
            <p>
                Ajanınızın kullanabileceği fonksiyonları burada yönetebilirsiniz. Bu fonksiyonlar aracılığıyla ajanınız
                çağrılar sırasında çeşitli işlemleri gerçekleştirebilir.
            </p>
        </div>

        <a-spin :spinning="loading">
            <!-- Fonksiyonlar listesi -->
            <div class="functions-section">
                <div class="section-header">
                    <h4>Fonksiyonlar</h4>
                    <PermissionController :permissions="['voice-agent.create']">
                        <SupsisButton primary @click="showAddTool" icon="add"> Fonksiyon Ekle</SupsisButton>
                    </PermissionController>
                </div>

                <!-- Fonksiyonlar listesi -->
                <div v-if="!loading && tools && tools.length > 0" class="tools-list">
                    <a-table
                        :columns="toolColumns"
                        :data-source="tools"
                        :pagination="false"
                        :loading="tableLoading"
                        rowKey="name"
                    >
                        <template slot="type" slot-scope="text, record">
                            <span>{{ getToolTypeText(record.type) }}</span>
                        </template>
                        <template slot="action" slot-scope="text, record">
                            <div class="actions">
                                <SupsisButton icon="visibility" small ghost @click="viewTool(record)"></SupsisButton>
                                <PermissionController :permissions="['voice-agent.update']">
                                    <SupsisButton icon="edit" small ghost @click="editTool(record)"></SupsisButton>
                                </PermissionController>
                                <PermissionController :permissions="['voice-agent.delete']">
                                    <SupsisButton
                                        danger
                                        icon="delete"
                                        small
                                        ghost
                                        @click="removeTool(record)"
                                    ></SupsisButton>
                                </PermissionController>
                            </div>
                        </template>
                    </a-table>
                </div>
                <div v-else-if="!loading" class="empty-state">
                    <a-empty description="Bu ajana tanımlanmış fonksiyon bulunmamaktadır">
                        <PermissionController :permissions="['voice-agent.create']">
                            <SupsisButton primary @click="showAddTool"> Fonksiyon Ekle</SupsisButton>
                        </PermissionController>
                    </a-empty>
                </div>
            </div>
        </a-spin>

        <!-- Fonksiyon Ekleme Modal -->
        <a-modal v-model="showToolTypeModal" title="Fonksiyon Türünü Seçin" :footer="null" @cancel="closeToolTypeModal">
            <div class="tool-type-selection">
                <div class="tool-type-list">
                    <div class="tool-type-option" @click="selectToolType('end_call')">
                        <div class="tool-icon"><i class="material-icons">call_end</i></div>
                        <div class="tool-info">
                            <div class="tool-name">End Call</div>
                            <div class="tool-description">Görüşmeyi sonlandırmak için kullanılır</div>
                        </div>
                    </div>
                    <div class="tool-type-option" @click="selectToolType('transfer_call')">
                        <div class="tool-icon"><i class="material-icons">call_forward</i></div>
                        <div class="tool-info">
                            <div class="tool-name">Call Transfer</div>
                            <div class="tool-description">Görüşmeyi başka bir numaraya aktarmak için kullanılır</div>
                        </div>
                    </div>
                    <div class="tool-type-option" @click="selectToolType('press_digit')">
                        <div class="tool-icon"><i class="material-icons">dialpad</i></div>
                        <div class="tool-info">
                            <div class="tool-name">Press Digit (IVR Navigation)</div>
                            <div class="tool-description">IVR menülerinde gezinmek için tuş basmak için kullanılır</div>
                        </div>
                    </div>
                    <div class="tool-type-option" @click="selectToolType('extract_dynamic_variable')">
                        <div class="tool-icon"><i class="material-icons">data_object</i></div>
                        <div class="tool-info">
                            <div class="tool-name">Extract Dynamic Variable</div>
                            <div class="tool-description">Konuşmadan dinamik değişkenler çıkarmak için kullanılır</div>
                        </div>
                    </div>
                    <div class="tool-type-option" @click="selectToolType('agent_swap')">
                        <div class="tool-icon"><i class="material-icons">swap_horiz</i></div>
                        <div class="tool-info">
                            <div class="tool-name">Agent Transfer</div>
                            <div class="tool-description">Görüşmeyi başka bir ajana aktarmak için kullanılır</div>
                        </div>
                    </div>
                    <div class="tool-type-option" @click="selectToolType('custom')">
                        <div class="tool-icon"><i class="material-icons">build</i></div>
                        <div class="tool-info">
                            <div class="tool-name">Custom Function</div>
                            <div class="tool-description">Özel fonksiyon tanımlamak için kullanılır</div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <SupsisButton @click="closeToolTypeModal">İptal</SupsisButton>
                </div>
            </div>
        </a-modal>

        <!-- End Call Form Modal -->
        <a-modal v-model="showEndCallModal" title="End Call Fonksiyonu" :footer="null" @cancel="closeEndCallModal">
            <div id="end-call-modal-wrapper">
                <a-form :form="endCallForm" layout="vertical" @submit.prevent>
                    <a-form-item label="Name">
                        <a-input
                            v-decorator="[
                                'name',
                                {
                                    initialValue: 'end_call',
                                    rules: [
                                        { required: true, message: 'İsim gereklidir!' },
                                        {
                                            pattern: /^[a-zA-Z0-9_-]+$/,
                                            message:
                                                'Sadece alfanumerik, alt çizgi ve tire karakterleri kullanılabilir!',
                                        },
                                    ],
                                },
                            ]"
                            placeholder="end_call"
                        />
                        <div class="hint-text">
                            Fonksiyon adı benzersiz olmalıdır, a-z, A-Z, 0-9, _ veya - karakterlerinden oluşabilir
                            (boşluk olmadan)
                        </div>
                    </a-form-item>

                    <a-form-item label="Description (Opsiyonel)">
                        <a-textarea
                            v-decorator="[
                                'description',
                                {
                                    initialValue: 'End the call with user.',
                                },
                            ]"
                            placeholder="Fonksiyonun ne yaptığını ve ne zaman çağrılacağını tanımlayın"
                            :rows="3"
                        />
                    </a-form-item>

                    <div class="modal-footer">
                        <SupsisButton @click="closeEndCallModal">İptal</SupsisButton>
                        <PermissionController :permissions="['voice-agent.update']">
                            <SupsisButton primary @click="handleEndCallSubmit" :disabled="endCallSubmitting">
                                <i v-if="endCallSubmitting" class="material-icons spin">autorenew</i>
                                Kaydet
                            </SupsisButton>
                        </PermissionController>
                    </div>
                </a-form>
            </div>
        </a-modal>

        <!-- Transfer Call Form Modal -->
        <a-modal
            v-model="showTransferCallModal"
            title="Call Transfer Fonksiyonu"
            :footer="null"
            @cancel="closeTransferCallModal"
            width="800px"
        >
            <div id="transfer-call-modal-wrapper">
                <a-form :form="transferCallForm" layout="vertical" @submit.prevent>
                    <a-form-item label="Name">
                        <a-input
                            v-decorator="[
                                'name',
                                {
                                    initialValue: 'transfer_call',
                                    rules: [
                                        { required: true, message: 'İsim gereklidir!' },
                                        {
                                            pattern: /^[a-zA-Z0-9_-]+$/,
                                            message:
                                                'Sadece alfanumerik, alt çizgi ve tire karakterleri kullanılabilir!',
                                        },
                                    ],
                                },
                            ]"
                            placeholder="transfer_call"
                        />
                        <div class="hint-text">
                            Fonksiyon adı benzersiz olmalıdır, a-z, A-Z, 0-9, _ veya - karakterlerinden oluşabilir
                            (boşluk olmadan)
                        </div>
                    </a-form-item>

                    <a-form-item label="Description (Opsiyonel)">
                        <a-textarea
                            v-decorator="[
                                'description',
                                {
                                    initialValue: 'Transfer the call to another number.',
                                },
                            ]"
                            placeholder="Fonksiyonun ne yaptığını ve ne zaman çağrılacağını tanımlayın"
                            :rows="3"
                        />
                    </a-form-item>

                    <a-form-item label="Transfer to">
                        <div class="transfer-destination-options">
                            <a-radio-group
                                v-decorator="[
                                    'destination_type',
                                    {
                                        initialValue: 'predefined',
                                    },
                                ]"
                                @change="onTransferTypeChange"
                            >
                                <a-radio value="predefined">Static Destination</a-radio>
                                <a-radio value="inferred">Dynamic Routing</a-radio>
                            </a-radio-group>
                            <a-select
                                v-decorator="[
                                    'number_format',
                                    {
                                        initialValue: 'e164',
                                    },
                                ]"
                                style="min-width: 180px"
                            >
                                <a-select-option value="e164">Format to E.164</a-select-option>
                                <a-select-option value="raw">Keep raw input</a-select-option>
                            </a-select>
                        </div>
                    </a-form-item>

                    <a-form-item
                        v-if="transferCallForm.getFieldValue('destination_type') === 'predefined'"
                        label="Static Destination"
                        required
                    >
                        <a-input
                            v-decorator="[
                                'number',
                                {
                                    rules: [
                                        {
                                            required:
                                                transferCallForm.getFieldValue('destination_type') === 'predefined',
                                            message: 'Transfer edilecek numara gereklidir!',
                                        },
                                    ],
                                },
                            ]"
                            placeholder="Enter a static phone number / SIP URI / dynamic variable."
                        />
                        <div class="transfer-extension-row">
                            <a-checkbox
                                v-decorator="[
                                    'has_extension',
                                    {
                                        valuePropName: 'checked',
                                        initialValue: false,
                                    },
                                ]"
                            >
                                Extension Number
                            </a-checkbox>
                            <a-input
                                v-if="transferCallForm.getFieldValue('has_extension')"
                                v-decorator="['extension', {}]"
                                placeholder="0000"
                                style="max-width: 200px"
                            />
                        </div>
                        <div class="hint-text">
                            Enter a static phone number, SIP URI, or dynamic variable (örn: transfer_number değişkeni).
                        </div>
                        <div
                            v-if="transferCallForm.getFieldValue('has_extension')"
                            class="hint-text"
                        >
                            Extension digits are sent via DTMF after the main number connects.
                        </div>
                    </a-form-item>

                    <a-form-item
                        v-if="transferCallForm.getFieldValue('destination_type') === 'inferred'"
                        label="Dynamic Routing Prompt"
                        required
                    >
                        <a-textarea
                            v-decorator="[
                                'prompt',
                                {
                                    rules: [
                                        {
                                            required: transferCallForm.getFieldValue('destination_type') === 'inferred',
                                            message: 'Yönlendirme için prompt gereklidir!',
                                        },
                                    ],
                                    initialValue:
                                        'Kullanıcı destek isterse +1 (925) 222-2222 numarasına yönlendir; satış ekibine ulaşmak isterse +1 (925) 333-3333 numarasına yönlendir',
                                },
                            ]"
                            placeholder="Kullanıcı destek isterse +1 (925) 222-2222 numarasına yönlendir; satış ekibine ulaşmak isterse +1 (925) 333-3333 numarasına yönlendir"
                            :rows="3"
                        />
                        <div class="hint-text">Use a prompt to handle dynamic call transfer routing.</div>
                    </a-form-item>

                    <a-form-item label="Type">
                        <a-radio-group
                            v-decorator="[
                                'transfer_type',
                                {
                                    initialValue: 'cold',
                                },
                            ]"
                            @change="onWarmTransferChange"
                        >
                            <a-radio value="cold">Cold Transfer</a-radio>
                            <a-radio value="warm">Warm Transfer</a-radio>
                        </a-radio-group>
                        <div class="hint-text">
                            <div v-if="transferCallForm.getFieldValue('transfer_type') === 'cold'">
                                AI transfers the call to the next agent without a debrief.
                            </div>
                            <div v-else>AI provides a debrief to the next agent</div>
                        </div>
                    </a-form-item>

                    <a-form-item
                        v-if="transferCallForm.getFieldValue('transfer_type') === 'warm'"
                        label="Warm Transfer Message"
                    >
                        <a-textarea
                            v-decorator="[
                                'warm_transfer_message',
                                {
                                    rules: [
                                        {
                                            required: transferCallForm.getFieldValue('transfer_type') === 'warm',
                                            message: 'Warm transfer mesajı gereklidir!',
                                        },
                                    ],
                                    initialValue: 'Müşteri şu konuda yardım istiyor...',
                                },
                            ]"
                            placeholder="Müşteri şu konuda yardım istiyor..."
                            :rows="2"
                        />
                        <div class="hint-text">Çağrı aktarılırken karşı tarafa iletilecek kısa bilgilendirme</div>
                    </a-form-item>

                    <a-form-item label="Displayed Phone Number">
                        <a-radio-group
                            v-decorator="[
                                'display_number',
                                {
                                    initialValue: false,
                                },
                            ]"
                        >
                            <a-radio :value="false">Supsis Agent's Number</a-radio>
                            <a-radio :value="true">Transferee's Number</a-radio>
                        </a-radio-group>
                        <div class="hint-text">
                            <div v-if="!transferCallForm.getFieldValue('display_number')">
                                Arayan numara olarak Supsis Agent numarası gösterilir
                            </div>
                            <div v-else>
                                Original Caller or Callee. Özel telefon kullanıyorsanız, SIP REFER ve PSTN transferini
                                etkinleştirin ve arayan numarasını göstermek için SIP REFER ayarlayın.
                            </div>
                        </div>
                    </a-form-item>

                    <a-form-item label="Custom SIP Headers">
                        <div class="key-value-editor">
                            <div
                                v-for="(header, index) in transferCallHeaders"
                                :key="index"
                                class="key-value-row"
                            >
                                <a-input
                                    v-model="header.key"
                                    placeholder="Header adı (örn: X-Custom-Header)"
                                    style="width: 40%"
                                />
                                <a-input
                                    v-model="header.value"
                                    placeholder="Header değeri (örn: Custom Value)"
                                    style="width: 50%"
                                />
                                <a-button
                                    type="danger"
                                    icon="delete"
                                    @click="removeTransferCallHeader(index)"
                                    style="width: 8%"
                                />
                            </div>
                            <a-button type="dashed" block @click="addTransferCallHeader">
                                <i class="material-icons">add</i> Header Ekle
                            </a-button>
                        </div>
                        <div class="hint-text">
                            Add key/value pairs for call routing, metadata, or carrier integration.
                        </div>
                    </a-form-item>

                    <div class="modal-footer">
                        <SupsisButton @click="closeTransferCallModal">İptal</SupsisButton>
                        <PermissionController :permissions="['voice-agent.update']">
                            <SupsisButton primary @click="handleTransferCallSubmit" :disabled="transferCallSubmitting">
                                <i v-if="transferCallSubmitting" class="material-icons spin">autorenew</i>
                                Kaydet
                            </SupsisButton>
                        </PermissionController>
                    </div>
                </a-form>
            </div>
        </a-modal>

        <!-- Press Digit Form Modal -->
        <a-modal
            v-model="showPressDigitModal"
            title="Press Digit Fonksiyonu"
            :footer="null"
            @cancel="closePressDigitModal"
        >
            <div id="press-digit-modal-wrapper">
                <a-form :form="pressDigitForm" layout="vertical" @submit.prevent>
                    <a-form-item label="Name">
                        <a-input
                            v-decorator="[
                                'name',
                                {
                                    rules: [
                                        { required: true, message: 'İsim gereklidir!' },
                                        {
                                            pattern: /^[a-zA-Z0-9_-]+$/,
                                            message:
                                                'Sadece alfanumerik, alt çizgi ve tire karakterleri kullanılabilir!',
                                        },
                                    ],
                                },
                            ]"
                            placeholder="press_digit"
                        />
                        <div class="hint-text">
                            Fonksiyon adı benzersiz olmalıdır, a-z, A-Z, 0-9, _ veya - karakterlerinden oluşabilir
                            (boşluk olmadan)
                        </div>
                    </a-form-item>

                    <a-form-item label="Description (Opsiyonel)">
                        <a-textarea
                            v-decorator="[
                                'description',
                                {
                                    initialValue: 'Navigate through IVR menu by pressing specific digit(s).',
                                },
                            ]"
                            placeholder="Fonksiyonun ne yaptığını ve ne zaman çağrılacağını tanımlayın"
                            :rows="3"
                        />
                    </a-form-item>

                    <a-form-item label="Pause Detection Delay (ms)">
                        <a-input-number
                            v-decorator="[
                                'pause_detection_delay',
                                {
                                    initialValue: 1000,
                                },
                            ]"
                            :min="0"
                            :step="100"
                            placeholder="1000"
                            style="width: 100%"
                        />
                        <div class="hint-text">Fonksiyon çağrıldığında ajanın bekleyeceği süre (milisaniye)</div>
                    </a-form-item>

                    <div class="modal-footer">
                        <SupsisButton @click="closePressDigitModal">İptal</SupsisButton>
                        <PermissionController :permissions="['voice-agent.update']">
                            <SupsisButton primary @click="handlePressDigitSubmit" :disabled="pressDigitSubmitting">
                                <i v-if="pressDigitSubmitting" class="material-icons spin">autorenew</i>
                                Kaydet
                            </SupsisButton>
                        </PermissionController>
                    </div>
                </a-form>
            </div>
        </a-modal>

        <!-- Custom Function Form Modal -->
        <a-modal
            v-model="showCustomFunctionModal"
            title="Custom Function"
            :footer="null"
            @cancel="closeCustomFunctionModal"
            width="800px"
        >
            <div id="custom-function-modal-wrapper">
                <a-form :form="customFunctionForm" layout="vertical" @submit.prevent>
                    <a-form-item label="Name">
                        <a-input
                            v-decorator="[
                                'name',
                                {
                                    rules: [
                                        { required: true, message: 'İsim gereklidir!' },
                                        {
                                            pattern: /^[a-zA-Z0-9_-]+$/,
                                            message:
                                                'Sadece alfanumerik, alt çizgi ve tire karakterleri kullanılabilir!',
                                        },
                                    ],
                                },
                            ]"
                            placeholder="check_availability"
                        />
                        <div class="hint-text">
                            Fonksiyon adı benzersiz olmalıdır, a-z, A-Z, 0-9, _ veya - karakterlerinden oluşabilir
                            (boşluk olmadan)
                        </div>
                    </a-form-item>

                    <a-form-item label="Description">
                        <a-textarea
                            v-decorator="[
                                'description',
                                {
                                    rules: [{ required: true, message: 'Açıklama gereklidir!' }],
                                },
                            ]"
                            placeholder="Fonksiyonun ne yaptığını ve ne zaman çağrılacağını tanımlayın"
                            :rows="3"
                        />
                    </a-form-item>

                    <a-form-item label="HTTP Method & URL">
                        <div class="url-input-group">
                            <a-select
                                v-decorator="[
                                    'method',
                                    {
                                        initialValue: 'POST',
                                    },
                                ]"
                                style="width: 120px"
                            >
                                <a-select-option value="GET">GET</a-select-option>
                                <a-select-option value="POST">POST</a-select-option>
                                <a-select-option value="PUT">PUT</a-select-option>
                                <a-select-option value="PATCH">PATCH</a-select-option>
                                <a-select-option value="DELETE">DELETE</a-select-option>
                            </a-select>
                            <a-input
                                v-decorator="[
                                    'url',
                                    {
                                        rules: [
                                            { required: true, message: 'URL gereklidir!' },
                                            { type: 'url', message: 'Geçerli bir URL girin!' },
                                        ],
                                    },
                                ]"
                                placeholder="https://api.example.com/function"
                            />
                            <SupsisButton type="primary" size="small" @click="showWebhookAutomationSelector">
                                <component
                                    :is="'AutomationIcon'"
                                    class="myIcon"
                                    :style="{ fill: 'var(--color-icon-primary-default)' }"
                                />
                                Supsis Otomasyon'dan Seç
                            </SupsisButton>
                        </div>

                        <!-- Seçilen otomasyon bilgisi -->
                        <div v-if="selectedAutomation" class="selected-automation">
                            <div class="automation-info">
                                <i class="material-icons">link</i>
                                <span>Bağlı Otomasyon: <strong>{{ selectedAutomation.name }}</strong></span>
                                <a-button type="link" size="small" @click="goToAutomationDetail(selectedAutomation.id)">
                                    <i class="material-icons">open_in_new</i> Düzenle
                                </a-button>
                            </div>
                        </div>
                    </a-form-item>

                    <!-- Headers -->
                    <a-form-item label="Headers (Opsiyonel)">
                        <div class="key-value-editor">
                            <div v-for="(header, index) in customFunctionHeaders" :key="index" class="key-value-row">
                                <a-input v-model="header.key" placeholder="Header adı (örn: Authorization)" style="width: 40%" />
                                <a-input v-model="header.value" placeholder="Header değeri (örn: Bearer token123)" style="width: 50%" />
                                <a-button type="danger" icon="delete" @click="removeHeader(index)" style="width: 8%" />
                            </div>
                            <a-button type="dashed" block @click="addHeader">
                                <i class="material-icons">add</i> Header Ekle
                            </a-button>
                        </div>
                        <div class="hint-text">API isteğine eklenecek HTTP header'ları (örn: Authorization, Content-Type)</div>
                    </a-form-item>

                    <!-- Query Parameters -->
                    <a-form-item label="Query Parameters (Opsiyonel)">
                        <div class="key-value-editor">
                            <div v-for="(param, index) in customFunctionQueryParams" :key="index" class="key-value-row">
                                <a-input v-model="param.key" placeholder="Parametre adı (örn: page)" style="width: 40%" />
                                <a-input v-model="param.value" placeholder="Parametre değeri (örn: 1)" style="width: 50%" />
                                <a-button type="danger" icon="delete" @click="removeQueryParam(index)" style="width: 8%" />
                            </div>
                            <a-button type="dashed" block @click="addQueryParam">
                                <i class="material-icons">add</i> Query Parameter Ekle
                            </a-button>
                        </div>
                        <div class="hint-text">URL'ye eklenecek query parametreleri (örn: ?page=1&sort=asc)</div>
                    </a-form-item>

                    <!-- Response Variables -->
                    <a-form-item label="Response Variables (Opsiyonel)">
                        <div class="key-value-editor">
                            <div v-for="(variable, index) in customFunctionResponseVariables" :key="index" class="key-value-row">
                                <a-input v-model="variable.key" placeholder="Değişken adı (örn: user_name)" style="width: 40%" />
                                <a-input v-model="variable.value" placeholder="JSON path (örn: data.user.name)" style="width: 50%" />
                                <a-button type="danger" icon="delete" @click="removeResponseVariable(index)" style="width: 8%" />
                            </div>
                            <a-button type="dashed" block @click="addResponseVariable">
                                <i class="material-icons">add</i> Response Variable Ekle
                            </a-button>
                        </div>
                        <div class="hint-text">API yanıtından çıkarılacak değişkenler (örn: user_name → data.user.name)</div>
                    </a-form-item>

                    <a-form-item label="API Timeout (ms)">
                        <a-input-number
                            v-decorator="[
                                'timeout_ms',
                                {
                                    initialValue: 60000,
                                },
                            ]"
                            :min="1000"
                            :max="600000"
                            :step="1000"
                            style="width: 100%"
                        />
                        <div class="hint-text">API çağrısının zamanaşımı süresi (1.000-600.000 ms arası)</div>
                    </a-form-item>

                    <a-row :gutter="16">
                        <a-col :span="12">
                            <a-form-item>
                                <a-checkbox
                                    v-decorator="[
                                        'speak_during_execution',
                                        {
                                            valuePropName: 'checked',
                                            initialValue: true,
                                        },
                                    ]"
                                >
                                    İşlem Sırasında Konuş
                                </a-checkbox>
                                <div class="hint-text">
                                    İşlem sırasında ajanın "Bir saniye, kontrol ediyorum" gibi cümleler söylemesine izin
                                    verir
                                </div>
                            </a-form-item>
                        </a-col>
                        <a-col :span="12">
                            <a-form-item>
                                <a-checkbox
                                    v-decorator="[
                                        'speak_after_execution',
                                        {
                                            valuePropName: 'checked',
                                            initialValue: true,
                                        },
                                    ]"
                                >
                                    İşlem Sonrası Konuş
                                </a-checkbox>
                                <div class="hint-text">
                                    İşlem sonuçlandığında ajanın sonucu kullanıcıya bildirmesine izin verir
                                </div>
                            </a-form-item>
                        </a-col>
                    </a-row>
                    <a-form-item
                        v-if="customFunctionForm.getFieldValue('speak_during_execution')"
                        label="İşlem Sırasında Konuşma Açıklaması (Opsiyonel)"
                    >
                        <a-textarea
                            v-decorator="['execution_message_description', {}]"
                            placeholder="Ajanın işlem sırasında ne söyleyeceğine dair açıklama veya örnekler"
                            :rows="2"
                        />
                        <div class="hint-text">Sadece "İşlem Sırasında Konuş" aktifse kullanılır</div>
                    </a-form-item>

                    <a-form-item label="Parameters (JSON Schema)">
                        <div class="parameters-toolbar">
                            <div class="sample-buttons">
                                <a-button size="small" @click="applySampleParameters(1)">Randevu Örneği</a-button>
                                <a-button size="small" @click="applySampleParameters(2)">Sipariş Örneği</a-button>
                                <a-button size="small" @click="applySampleParameters(3)"
                                    >Kullanıcı Bilgisi Örneği
                                </a-button>
                            </div>
                        </div>
                        <a-textarea
                            v-decorator="[
                                'parameters',
                                {
                                    rules: [
                                        { required: true, message: 'Parametreler gereklidir!' },
                                        { validator: validateParameters },
                                    ],
                                },
                            ]"
                            placeholder="JSON Schema formatında parametreler"
                            :rows="10"
                        />
                        <div class="hint-text">
                            Bu fonksiyona geçirilecek parametrelerin JSON Schema formatında tanımı
                        </div>
                    </a-form-item>

                    <div class="modal-footer">
                        <SupsisButton @click="closeCustomFunctionModal">İptal</SupsisButton>
                        <SupsisButton primary @click="handleCustomFunctionSubmit" :disabled="customFunctionSubmitting">
                            <i v-if="customFunctionSubmitting" class="material-icons spin">autorenew</i>
                            Kaydet
                        </SupsisButton>
                    </div>
                </a-form>
            </div>
        </a-modal>

        <!-- Extract Dynamic Variable Form Modal -->
        <a-modal
            v-model="showExtractDynamicVariableModal"
            title="Extract Dynamic Variable"
            :footer="null"
            @cancel="closeExtractDynamicVariableModal"
            width="800px"
        >
            <div id="extract-dynamic-variable-modal-wrapper">
                <a-form :form="extractDynamicVariableForm" layout="vertical" @submit.prevent>
                    <a-form-item label="Function Name">
                        <a-input
                            v-decorator="[
                                'name',
                                {
                                    initialValue: 'extract_user_details',
                                    rules: [
                                        { required: true, message: 'İsim gereklidir!' },
                                        {
                                            pattern: /^[a-zA-Z0-9_-]+$/,
                                            message:
                                                'Sadece alfanumerik, alt çizgi ve tire karakterleri kullanılabilir!',
                                        },
                                    ],
                                },
                            ]"
                            placeholder="extract_user_details"
                        />
                        <div class="hint-text">
                            Fonksiyon adı benzersiz olmalıdır, a-z, A-Z, 0-9, _ veya - karakterlerinden oluşabilir
                            (boşluk olmadan)
                        </div>
                    </a-form-item>

                    <a-form-item label="Description">
                        <a-textarea
                            v-decorator="[
                                'description',
                                {
                                    initialValue: 'Extract the user\'s details like name, email, age, etc. from the conversation',
                                    rules: [{ required: true, message: 'Açıklama gereklidir!' }],
                                },
                            ]"
                            placeholder="Extract the user's details like name, email, age, etc. from the conversation"
                            :rows="3"
                        />
                    </a-form-item>

                    <a-form-item label="Variables">
                        <div class="variables-list">
                            <div v-for="(variable, index) in extractDynamicVariables" :key="index" class="variable-item">
                                <div class="variable-header">
                                    <h5>Variable {{ index + 1 }}</h5>
                                    <a-button type="danger" size="small" icon="delete" @click="removeExtractVariable(index)">
                                        Sil
                                    </a-button>
                                </div>
                                <a-row :gutter="16">
                                    <a-col :span="12">
                                        <a-form-item label="Type">
                                            <a-select v-model="variable.type" placeholder="Seçiniz">
                                                <a-select-option value="string">String</a-select-option>
                                            </a-select>
                                        </a-form-item>
                                    </a-col>
                                    <a-col :span="12">
                                        <a-form-item label="Name">
                                            <a-input v-model="variable.name" placeholder="customer_name" />
                                        </a-form-item>
                                    </a-col>
                                </a-row>
                                <a-form-item label="Description">
                                    <a-input v-model="variable.description" placeholder="The name of the customer." />
                                </a-form-item>
                                <a-form-item label="Examples (virgülle ayırın)">
                                    <a-input
                                        v-model="variable.examplesText"
                                        placeholder="John Doe, Jane Smith"
                                    />
                                    <div class="hint-text">Modele stil ve sözdizimi öğretmek için örnek değerler</div>
                                </a-form-item>
                            </div>
                            <a-button type="dashed" block @click="addExtractVariable">
                                <i class="material-icons">add</i> Variable Ekle
                            </a-button>
                        </div>
                    </a-form-item>

                    <div class="modal-footer">
                        <SupsisButton @click="closeExtractDynamicVariableModal">İptal</SupsisButton>
                        <PermissionController :permissions="['voice-agent.update']">
                            <SupsisButton primary @click="handleExtractDynamicVariableSubmit" :disabled="extractDynamicVariableSubmitting">
                                <i v-if="extractDynamicVariableSubmitting" class="material-icons spin">autorenew</i>
                                Kaydet
                            </SupsisButton>
                        </PermissionController>
                    </div>
                </a-form>
            </div>
        </a-modal>

        <!-- Agent Swap Form Modal -->
        <a-modal
            v-model="showAgentSwapModal"
            title="Agent Transfer"
            :footer="null"
            @cancel="closeAgentSwapModal"
            width="800px"
        >
            <div id="agent-swap-modal-wrapper">
                <a-form :form="agentSwapForm" layout="vertical" @submit.prevent>
                    <a-form-item label="Function Name">
                        <a-input
                            v-decorator="[
                                'name',
                                {
                                    initialValue: 'agent_transfer',
                                    rules: [
                                        { required: true, message: 'İsim gereklidir!' },
                                        {
                                            pattern: /^[a-zA-Z0-9_-]+$/,
                                            message:
                                                'Sadece alfanumerik, alt çizgi ve tire karakterleri kullanılabilir!',
                                        },
                                    ],
                                },
                            ]"
                            placeholder="agent_transfer"
                        />
                        <div class="hint-text">
                            Fonksiyon adı benzersiz olmalıdır, a-z, A-Z, 0-9, _ veya - karakterlerinden oluşabilir
                            (boşluk olmadan)
                        </div>
                    </a-form-item>

                    <a-form-item label="Description">
                        <a-textarea
                            v-decorator="[
                                'description',
                                {
                                    initialValue: 'Transfer the call to the customer service agent',
                                    rules: [{ required: true, message: 'Açıklama gereklidir!' }],
                                },
                            ]"
                            placeholder="Transfer the call to the customer service agent"
                            :rows="3"
                        />
                    </a-form-item>

                    <a-form-item label="Select Agent" required>
                        <a-select
                            v-decorator="[
                                'agent_id',
                                {
                                    rules: [{ required: true, message: 'Ajan seçilmelidir!' }],
                                },
                            ]"
                            placeholder="Bir ajan seçin"
                            show-search
                            :filter-option="filterAgentOption"
                        >
                            <a-select-option v-for="ag in availableAgents" :key="ag.agent_id" :value="ag.agent_id">
                                {{ ag.agent_name }}
                            </a-select-option>
                        </a-select>
                    </a-form-item>

                    <a-form-item label="Post Call Analysis Setting">
                        <a-radio-group
                            v-decorator="[
                                'post_call_analysis_setting',
                                {
                                    initialValue: 'only_destination_agent',
                                },
                            ]"
                        >
                            <a-radio value="both_agents">Both Agents</a-radio>
                            <a-radio value="only_destination_agent">Only Destination Agent</a-radio>
                        </a-radio-group>
                        <div class="hint-text">
                            Hangi ajanın analiz alanlarının çağrı geçmişine dahil edileceğini seçin
                        </div>
                    </a-form-item>

                    <a-form-item label="Webhook Setting">
                        <a-radio-group
                            v-decorator="[
                                'webhook_setting',
                                {
                                    initialValue: 'only_source_agent',
                                },
                            ]"
                        >
                            <a-radio value="both_agents">Both Agents</a-radio>
                            <a-radio value="only_destination_agent">Only Destination Agent</a-radio>
                            <a-radio value="only_source_agent">Only Source Agent</a-radio>
                        </a-radio-group>
                        <div class="hint-text">
                            Çağrı güncellemeleri göndermek için hangi ajanın webhook endpoint'inin kullanılacağını seçin
                        </div>
                    </a-form-item>

                    <a-form-item>
                        <a-checkbox
                            v-decorator="[
                                'speak_during_execution',
                                {
                                    valuePropName: 'checked',
                                    initialValue: false,
                                },
                            ]"
                        >
                            İşlem Sırasında Konuş
                        </a-checkbox>
                        <div class="hint-text">
                            Fonksiyon 2 saniyeden uzun sürerse, ajan "Bunu kontrol etmeme izin ver" gibi bir şey söyleyebilir
                        </div>
                    </a-form-item>

                    <a-form-item
                        v-if="agentSwapForm.getFieldValue('speak_during_execution')"
                        label="İşlem Sırasında Konuşma Açıklaması"
                    >
                        <a-textarea
                            v-decorator="['execution_message_description', {}]"
                            placeholder="Ajanın işlem sırasında ne söyleyeceğine dair açıklama"
                            :rows="2"
                        />
                        <div class="hint-text">Sadece "İşlem Sırasında Konuş" aktifse kullanılır</div>
                    </a-form-item>

                    <div class="modal-footer">
                        <SupsisButton @click="closeAgentSwapModal">İptal</SupsisButton>
                        <PermissionController :permissions="['voice-agent.update']">
                            <SupsisButton primary @click="handleAgentSwapSubmit" :disabled="agentSwapSubmitting">
                                <i v-if="agentSwapSubmitting" class="material-icons spin">autorenew</i>
                                Kaydet
                            </SupsisButton>
                        </PermissionController>
                    </div>
                </a-form>
            </div>
        </a-modal>

        <!-- Webhook Automation Selector Modal -->
        <a-modal
            v-model="showWebhookAutomationSelectorModal"
            title="Webhook Otomasyonları"
            :footer="null"
            @cancel="closeWebhookAutomationSelectorModal"
            width="800px"
        >
            <a-spin :spinning="webhookAutomationLoading">
                <div class="webhook-automation-list">
                    <div class="webhook-header">
                        <h4>Webhook Otomasyonları</h4>
                        <div class="search-input">
                            <a-input v-model="searchQuery" placeholder="Arama"></a-input>
                        </div>
                    </div>
                    <SupsisTable
                        v-if="filteredWebhookAutomations.length > 0"
                        :columns="webhookAutomationColumns"
                        :rows="filteredWebhookAutomations"
                        :options="{}"
                    >
                        <template v-slot:action="{ row }">
                            <SupsisButton type="primary" size="small" @click="selectWebhookAutomation(row)">
                                Seç
                            </SupsisButton>
                        </template>
                    </SupsisTable>
                    <div v-else class="no-webhooks">
                        <a-empty description="Webhook otomasyonu bulunamadı">
                            <SupsisButton type="primary" @click="goToAutomations">
                                <i class="material-icons" style="margin-right: 4px; font-size: 14px">add</i>
                                Yeni Webhook Otomasyonu Oluştur
                            </SupsisButton>
                        </a-empty>
                    </div>
                </div>
            </a-spin>
        </a-modal>
    </div>
</template>

<script>
import { VOICE_AGENT_API } from "../../../../../../../api/voiceAgent";
import SupsisButton from "@/views/Pages/Settings/components/SupsisButton.vue";
import _ from "lodash";
import CONFIG from "@/config/config";
import moment from "moment";
import { mapState } from "vuex";
import SupsisTable from "@/components/SupsisTable.vue";

export default {
    name: "FunctionsTab",
    components: {
        SupsisButton,
        SupsisTable,
    },
    props: {
        agent: {
            type: Object,
            default: () => null,
        },
    },
    computed: {
        ...mapState({
            automations: (state) => state.automation.automations,
        }),
        webhookAutomations() {
            return (this.automations || [])
                .filter((item) => _.get(item, "options.trigger") === "webhook")
                .map((item) => ({
                    id: item._id,
                    name: item.name,
                    createdAt: item.createdAt,
                    webhookId: _.get(item, "options.webhook.id"),
                    siteId: item.siteId,
                }));
        },
        filteredWebhookAutomations() {
            if (!this.searchQuery) {
                return this.webhookAutomations;
            }

            const query = this.searchQuery.toLowerCase();
            return this.webhookAutomations.filter((item) => item.name.toLowerCase().includes(query));
        },
    },
    data() {
        return {
            loading: false,
            tableLoading: false,
            tools: [],
            showToolTypeModal: false,

            // End Call Form
            showEndCallModal: false,
            endCallForm: this.$form.createForm(this),
            endCallSubmitting: false,

            // Transfer Call Form
            showTransferCallModal: false,
            transferCallForm: this.$form.createForm(this),
            transferCallSubmitting: false,
            transferCallHeaders: [],

            // Press Digit Form
            showPressDigitModal: false,
            pressDigitForm: this.$form.createForm(this),
            pressDigitSubmitting: false,

            // Custom Function Form
            showCustomFunctionModal: false,
            customFunctionForm: this.$form.createForm(this),
            customFunctionSubmitting: false,

            // Extract Dynamic Variable Form
            showExtractDynamicVariableModal: false,
            extractDynamicVariableForm: this.$form.createForm(this),
            extractDynamicVariableSubmitting: false,
            extractDynamicVariables: [],

            // Agent Swap Form
            showAgentSwapModal: false,
            agentSwapForm: this.$form.createForm(this),
            agentSwapSubmitting: false,
            availableAgents: [],

            // Webhook Automation Selector
            showWebhookAutomationSelectorModal: false,
            webhookAutomationLoading: false,
            searchQuery: "",
            webhookAutomationColumns: [
                {
                    title: "Otomasyon Adı",
                    field: "name",
                    key: "name",
                },
                {
                    title: "Oluşturulma Tarihi",
                    key: "createdAt",
                    field: "createdAt",
                    renderBodyCell: ({ row }) => {
                        return this.getWebhookAutomationDate(row.createdAt);
                    },
                },
                {
                    title: "İşlemler",
                    key: "action",
                    width: 100,
                    renderBodyCell: ({ row }) => {
                        return (
                            <SupsisButton type="primary" size="small" onClick={() => this.selectWebhookAutomation(row)}>
                                Seç
                            </SupsisButton>
                        );
                    },
                },
            ],

            // Table columns
            toolColumns: [
                {
                    title: "İsim",
                    dataIndex: "name",
                    key: "name",
                },
                {
                    title: "Tür",
                    key: "type",
                    scopedSlots: { customRender: "type" },
                },
                {
                    title: "Açıklama",
                    dataIndex: "description",
                    key: "description",
                    ellipsis: true,
                },
                {
                    title: "İşlemler",
                    key: "action",
                    scopedSlots: { customRender: "action" },
                    width: 120,
                },
            ],

            sampleParameters: [
                // Randevu örneği
                {
                    type: "object",
                    properties: {
                        randevu_tarihi: {
                            type: "string",
                            description: "Randevu için uygun olan tarihin zaman damgası.",
                        },
                        doktor_adi: {
                            type: "string",
                            description: "İsteğe bağlı olarak doktorun adını belirtmek için.",
                        },
                    },
                    required: ["randevu_tarihi"],
                },
                // Sipariş örneği
                {
                    type: "object",
                    properties: {
                        siparis_id: {
                            type: "string",
                            description: "Sorgulanacak siparişin ID'si.",
                        },
                        musteri_telefon: {
                            type: "string",
                            description: "Müşterinin telefon numarası.",
                        },
                        detay_seviyesi: {
                            type: "integer",
                            description: "Sipariş detayının seviyesi (1-3 arası).",
                        },
                    },
                    required: ["siparis_id"],
                },
                // Kullanıcı bilgisi örneği
                {
                    type: "object",
                    properties: {
                        email: {
                            type: "string",
                            description: "Kullanıcının e-posta adresi.",
                        },
                        isim: {
                            type: "string",
                            description: "Kullanıcının adı.",
                        },
                        soyisim: {
                            type: "string",
                            description: "Kullanıcının soyadı.",
                        },
                        bildirim_tercihi: {
                            type: "boolean",
                            description: "Kullanıcının bildirim almak isteyip istemediği.",
                        },
                    },
                    required: ["email"],
                },
            ],
            isEditing: false,
            editingTool: null,

            // Custom Function için yeni alanlar
            customFunctionHeaders: [],
            customFunctionQueryParams: [],
            customFunctionResponseVariables: [],
            selectedAutomation: null, // Seçilen otomasyon bilgisi
        };
    },
    watch: {
        agent: {
            handler(newValue, oldValue) {
                if (newValue) {
                    // Agent değiştiğinde veya llm içeriği değiştiğinde fonksiyonları yükle
                    // Eğer llm değişmişse (id aynı olsa bile içerik değişmişse) yeniden yükle
                    const isLLMChanged =
                        !oldValue ||
                        !oldValue.llm ||
                        JSON.stringify(newValue?.llm?.general_tools) !== JSON.stringify(oldValue?.llm?.general_tools);

                    if (isLLMChanged) {
                        console.log("Agent veya LLM değişti, fonksiyonlar yeniden yükleniyor...");
                        this.loadTools();
                    }
                }
            },
            immediate: true,
            deep: true,
        },
    },
    created() {
        // Component oluşturulduğunda otomasyon verilerini güncelle
        this.$store.dispatch("automation/syncAutomations");
    },
    methods: {
        loadTools() {
            if (!this.agent || !this.agent.llm) {
                this.tools = [];
                return;
            }

            try {
                this.loading = true;

                // General tools'ları al
                let generalTools = _.get(this.agent, "llm.general_tools", []) || [];

                // State tools'ları da topla
                let stateTools = [];
                const states = _.get(this.agent, "llm.states", []) || [];

                states.forEach((state) => {
                    const stateToolsList = _.get(state, "tools", []) || [];
                    stateToolsList.forEach((tool) => {
                        // Hangi state'e ait olduğu bilgisini ekle
                        tool.state_name = state.name;
                        stateTools.push(tool);
                    });
                });

                // Tüm tools'ları birleştir
                this.tools = [...generalTools, ...stateTools];
            } catch (error) {
                console.error("Fonksiyonlar yüklenirken hata:", error);
                this.$message.error("Fonksiyonlar yüklenirken bir hata oluştu");
            } finally {
                this.loading = false;
            }
        },
        showAddTool() {
            this.showToolTypeModal = true;
        },
        closeToolTypeModal() {
            this.showToolTypeModal = false;
        },
        selectToolType(type) {
            this.closeToolTypeModal();

            // Seçilen türe göre doğru form modalını göster
            if (type === "end_call") {
                this.showEndCallModal = true;
            } else if (type === "transfer_call") {
                this.transferCallHeaders = [];
                this.showTransferCallModal = true;
            } else if (type === "press_digit") {
                this.showPressDigitModal = true;
            } else if (type === "extract_dynamic_variable") {
                this.showExtractDynamicVariableModal = true;
            } else if (type === "agent_swap") {
                this.showAgentSwapModal = true;
                this.fetchAvailableAgents();
            } else if (type === "custom") {
                this.showCustomFunctionModal = true;
            }
        },
        getToolTypeText(type) {
            const types = {
                end_call: "End Call",
                transfer_call: "Call Transfer",
                press_digit: "Press Digit (IVR)",
                extract_dynamic_variable: "Extract Dynamic Variable",
                agent_swap: "Agent Transfer",
                custom: "Custom Function",
            };

            return types[type] || type;
        },
        viewTool(tool) {
            // Tool türüne göre uygun modalı aç ve formları doldur
            if (tool.type === "end_call") {
                this.endCallForm.setFieldsValue({
                    name: tool.name,
                    description: tool.description,
                });
                this.showEndCallModal = true;
            } else if (tool.type === "transfer_call") {
                const transferOption = tool.transfer_option || {};
                const isWarmTransfer = (transferOption.type || "").toLowerCase() === "warm_transfer";
                const formValues = {
                    name: tool.name,
                    description: tool.description,
                    number_format: tool.ignore_e164_validation ? "raw" : "e164",
                    has_extension: false,
                    transfer_type: isWarmTransfer ? "warm" : "cold",
                };

                // Transfer Destination türünü belirle
                if (tool.transfer_destination) {
                    formValues.destination_type = tool.transfer_destination.type;

                    if (tool.transfer_destination.type === "predefined") {
                        formValues.number = tool.transfer_destination.number;
                        formValues.extension = tool.transfer_destination.extension || "";
                        formValues.has_extension = !!tool.transfer_destination.extension;
                    } else if (tool.transfer_destination.type === "inferred") {
                        formValues.prompt = tool.transfer_destination.prompt;
                        formValues.extension = "";
                    }
                } else if (tool.number) {
                    // Eski formatta kaydedilmiş araçları desteklemek için
                    formValues.destination_type = "predefined";
                    formValues.number = tool.number;
                    formValues.extension = "";
                    formValues.has_extension = false;
                }

                // Warm/Cold Transfer tipini belirle
                if (isWarmTransfer) {
                    formValues.warm_transfer_message =
                        transferOption.warm_transfer_message ||
                        tool.warm_transfer_option?.message ||
                        "";
                    formValues.display_number = false;
                } else {
                    formValues.warm_transfer_message = "";
                    const showCaller =
                        typeof transferOption.show_transferee_as_caller === "boolean"
                            ? transferOption.show_transferee_as_caller
                            : !!tool.show_transferee_as_caller;
                    formValues.display_number = showCaller;
                }

                this.transferCallForm.setFieldsValue(formValues);
                this.loadTransferCallHeaders(tool);
                this.showTransferCallModal = true;
            } else if (tool.type === "press_digit") {
                this.pressDigitForm.setFieldsValue({
                    name: tool.name,
                    description: tool.description,
                    pause_detection_delay: tool.pause_detection_delay || 1000,
                });
                this.showPressDigitModal = true;
            } else if (tool.type === "extract_dynamic_variable") {
                if (tool.variables && Array.isArray(tool.variables)) {
                    this.extractDynamicVariables = tool.variables.map((v) => ({
                        type: v.type,
                        name: v.name,
                        description: v.description,
                        examplesText: Array.isArray(v.examples) ? v.examples.join(", ") : "",
                    }));
                } else {
                    this.extractDynamicVariables = [];
                }

                this.showExtractDynamicVariableModal = true;
                this.$nextTick(() => {
                    this.extractDynamicVariableForm.setFieldsValue({
                        name: tool.name,
                        description: tool.description,
                    });
                });
            } else if (tool.type === "agent_swap") {
                this.showAgentSwapModal = true;
                this.fetchAvailableAgents();
                this.$nextTick(() => {
                    const speakDuringExecution = !!tool.speak_during_execution;
                    const formValues = {
                        name: tool.name,
                        description: tool.description,
                        agent_id: tool.agent_id,
                        post_call_analysis_setting: tool.post_call_analysis_setting || 'only_destination_agent',
                        webhook_setting: tool.webhook_setting || 'only_source_agent',
                        speak_during_execution: speakDuringExecution,
                    };

                    this.agentSwapForm.setFieldsValue(formValues);

                    if (speakDuringExecution) {
                        this.$nextTick(() => {
                            this.agentSwapForm.setFieldsValue({
                                execution_message_description: tool.execution_message_description || '',
                            });
                        });
                    }
                });
            } else if (tool.type === "custom") {
                this.showCustomFunctionModal = true;

                this.$nextTick(() => {
                    this.customFunctionForm.setFieldsValue({
                        name: tool.name,
                        description: tool.description,
                        url: tool.url,
                        timeout_ms: tool.timeout_ms || 60000,
                        speak_during_execution: tool.speak_during_execution !== false,
                        speak_after_execution: tool.speak_after_execution !== false,
                        execution_message_description: tool.execution_message_description || "",
                        parameters:
                            typeof tool.parameters === "object" ? JSON.stringify(tool.parameters, null, 2) : "{}",
                    });

                    // Key-value editor'ları doldur
                    this.loadKeyValueEditors(tool);
                });
            }
        },
        editTool(tool) {
            // Düzenleme için aynı modalları kullanıyoruz ama isEditing bayrağını set ediyoruz
            this.isEditing = true;
            this.editingTool = tool;

            // Tool türüne göre uygun modalı aç ve formları doldur
            if (tool.type === "end_call") {
                this.endCallForm.setFieldsValue({
                    name: tool.name,
                    description: tool.description,
                });
                this.showEndCallModal = true;
            } else if (tool.type === "transfer_call") {
                const transferOption = tool.transfer_option || {};
                const isWarmTransfer = (transferOption.type || "").toLowerCase() === "warm_transfer";
                const formValues = {
                    name: tool.name,
                    description: tool.description,
                    number_format: tool.ignore_e164_validation ? "raw" : "e164",
                    has_extension: false,
                    transfer_type: isWarmTransfer ? "warm" : "cold",
                };

                if (tool.transfer_destination) {
                    formValues.destination_type = tool.transfer_destination.type;

                    if (tool.transfer_destination.type === "predefined") {
                        formValues.number = tool.transfer_destination.number;
                        formValues.extension = tool.transfer_destination.extension || "";
                        formValues.has_extension = !!tool.transfer_destination.extension;
                    } else if (tool.transfer_destination.type === "inferred") {
                        formValues.prompt = tool.transfer_destination.prompt;
                        formValues.extension = "";
                    }
                } else if (tool.number) {
                    formValues.destination_type = "predefined";
                    formValues.number = tool.number;
                    formValues.extension = "";
                    formValues.has_extension = false;
                }

                if (isWarmTransfer) {
                    formValues.warm_transfer_message =
                        transferOption.warm_transfer_message ||
                        tool.warm_transfer_option?.message ||
                        "";
                    formValues.display_number = false;
                } else {
                    formValues.warm_transfer_message = "";
                    const showCaller =
                        typeof transferOption.show_transferee_as_caller === "boolean"
                            ? transferOption.show_transferee_as_caller
                            : !!tool.show_transferee_as_caller;
                    formValues.display_number = showCaller;
                }

                this.transferCallForm.setFieldsValue(formValues);
                this.loadTransferCallHeaders(tool);
                this.showTransferCallModal = true;
            } else if (tool.type === "press_digit") {
                this.pressDigitForm.setFieldsValue({
                    name: tool.name,
                    description: tool.description,
                    pause_detection_delay: tool.pause_detection_delay || 1000,
                });
                this.showPressDigitModal = true;
            } else if (tool.type === "extract_dynamic_variable") {
                if (tool.variables && Array.isArray(tool.variables)) {
                    this.extractDynamicVariables = tool.variables.map((v) => ({
                        type: v.type,
                        name: v.name,
                        description: v.description,
                        examplesText: Array.isArray(v.examples) ? v.examples.join(", ") : "",
                    }));
                } else {
                    this.extractDynamicVariables = [];
                }

                this.showExtractDynamicVariableModal = true;
                this.$nextTick(() => {
                    this.extractDynamicVariableForm.setFieldsValue({
                        name: tool.name,
                        description: tool.description,
                    });
                });
            } else if (tool.type === "agent_swap") {
                this.showAgentSwapModal = true;
                this.fetchAvailableAgents();
                this.$nextTick(() => {
                    const speakDuringExecution = !!tool.speak_during_execution;
                    const formValues = {
                        name: tool.name,
                        description: tool.description,
                        agent_id: tool.agent_id,
                        post_call_analysis_setting: tool.post_call_analysis_setting || 'only_destination_agent',
                        webhook_setting: tool.webhook_setting || 'only_source_agent',
                        speak_during_execution: speakDuringExecution,
                    };

                    this.agentSwapForm.setFieldsValue(formValues);

                    if (speakDuringExecution) {
                        this.$nextTick(() => {
                            this.agentSwapForm.setFieldsValue({
                                execution_message_description: tool.execution_message_description || '',
                            });
                        });
                    }
                });
            } else if (tool.type === "custom") {
                this.showCustomFunctionModal = true;

                this.$nextTick(() => {
                    this.customFunctionForm.setFieldsValue({
                        name: tool.name,
                        description: tool.description,
                        url: tool.url,
                        timeout_ms: tool.timeout_ms || 60000,
                        speak_during_execution: tool.speak_during_execution !== false,
                        speak_after_execution: tool.speak_after_execution !== false,
                        execution_message_description: tool.execution_message_description || "",
                        parameters:
                            typeof tool.parameters === "object" ? JSON.stringify(tool.parameters, null, 2) : "{}",
                    });

                    // Key-value editor'ları doldur
                    this.loadKeyValueEditors(tool);
                });
            }
        },
        removeTool(tool) {
            if (!this.agent || !this.agent.llm) {
                return;
            }

            // Kullanıcıya onay sor
            this.$confirm({
                title: "Fonksiyonu Kaldır",
                content: `"${tool.name}" fonksiyonunu kaldırmak istediğinize emin misiniz?`,
                okText: "Evet",
                cancelText: "Hayır",
                okType: "danger",
                onOk: async () => {
                    try {
                        this.loading = true;

                        // General tools veya state tools içerisinden kaldır
                        let updatedLlmData = {};

                        if (tool.state_name) {
                            // State tool ise, state.tools dizisinden çıkar
                            const states = _.cloneDeep(this.agent.llm.states) || [];
                            const stateIndex = states.findIndex((s) => s.name === tool.state_name);

                            if (stateIndex >= 0) {
                                const state = states[stateIndex];
                                const updatedTools = (state.tools || []).filter((t) => t.name !== tool.name);
                                state.tools = updatedTools;
                                states[stateIndex] = state;

                                updatedLlmData.states = states;
                            }
                        } else {
                            // General tool ise, general_tools dizisinden çıkar
                            const generalTools = (_.get(this.agent, "llm.general_tools", []) || []).filter(
                                (t) => t.name !== tool.name
                            );
                            updatedLlmData.general_tools = generalTools;
                        }

                        // LLM güncelleme API çağrısı
                        await VOICE_AGENT_API.llms.update(this.agent.response_engine.llm_id, updatedLlmData);

                        // UI ve kullanıcıya bildirim
                        this.$message.success("Fonksiyon başarıyla kaldırıldı");

                        // Üst bileşene bildir
                        this.$emit("llm-updated", {
                            ...this.agent.llm,
                            ...updatedLlmData,
                        });

                        // Tools listesini yeniden yükle
                        this.loadTools();
                    } catch (error) {
                        console.error("Fonksiyon kaldırılırken hata:", error);
                        this.$message.error("Fonksiyon kaldırılırken bir hata oluştu");
                    } finally {
                        this.loading = false;
                    }
                },
                onCancel: () => {
                    // İşlem kullanıcı tarafından iptal edildi
                },
            });
        },

        // End Call Form Methods
        closeEndCallModal() {
            this.showEndCallModal = false;
            this.endCallForm.resetFields();
            this.isEditing = false;
            this.editingTool = null;
        },
        handleEndCallSubmit() {
            this.endCallForm.validateFields(async (err, values) => {
                if (!err) {
                    try {
                        this.endCallSubmitting = true;

                        const toolData = {
                            type: "end_call",
                            name: values.name,
                            description: values.description,
                        };

                        // Düzenleme mi ekleme mi kontrol et
                        if (this.isEditing && this.editingTool) {
                            // Eski adını koru (editingTool'dan)
                            await this.updateToolInLLM(this.editingTool.name, toolData);
                        } else {
                            await this.saveToolToLLM(toolData);
                        }

                        this.closeEndCallModal();
                    } catch (error) {
                        console.error("End Call fonksiyonu kaydedilirken hata:", error);
                        this.$message.error("Fonksiyon kaydedilirken bir hata oluştu");
                    } finally {
                        this.endCallSubmitting = false;
                    }
                }
            });
        },

        // Transfer Call Form Methods
        closeTransferCallModal() {
            this.showTransferCallModal = false;
            this.transferCallForm.resetFields();
            this.transferCallHeaders = [];
            this.isEditing = false;
            this.editingTool = null;
        },
        handleTransferCallSubmit() {
            this.transferCallForm.validateFields(async (err, values) => {
                if (!err) {
                    if (
                        values.destination_type === "predefined" &&
                        values.has_extension &&
                        (!values.extension || values.extension.trim().length === 0)
                    ) {
                        this.$message.error("Extension numarası gereklidir!");
                        return;
                    }

                    try {
                        this.transferCallSubmitting = true;

                        const toolData = {
                            type: "transfer_call",
                            name: values.name,
                            description: values.description,
                        };

                        // Transfer destination tipine göre veri ekle
                        if (values.destination_type === "predefined") {
                            toolData.transfer_destination = {
                                type: "predefined",
                                number: values.number ? values.number.trim() : "",
                            };

                            if (values.has_extension && values.extension) {
                                toolData.transfer_destination.extension = values.extension.trim();
                            }
                        } else if (values.destination_type === "inferred") {
                            toolData.transfer_destination = {
                                type: "inferred",
                                prompt: values.prompt,
                            };
                        }

                        toolData.ignore_e164_validation = values.number_format === "raw";

                        const isWarmTransfer = values.transfer_type === "warm";
                        toolData.transfer_option = {
                            type: isWarmTransfer ? "warm_transfer" : "cold_transfer",
                        };

                        if (isWarmTransfer) {
                            const warmMessage = values.warm_transfer_message ? values.warm_transfer_message.trim() : "";
                            toolData.transfer_option.warm_transfer_message = warmMessage;
                            toolData.warm_transfer_option = {
                                type: "static_message",
                                message: warmMessage,
                            };
                            delete toolData.show_transferee_as_caller;
                        } else {
                            const showCaller = !!values.display_number;
                            toolData.transfer_option.show_transferee_as_caller = showCaller;
                            toolData.show_transferee_as_caller = showCaller;
                            delete toolData.warm_transfer_option;
                        }

                        const sipHeaders = {};
                        let sipHeaderError = null;
                        this.transferCallHeaders.forEach((header) => {
                            const headerKey = header.key ? header.key.trim() : "";
                            const headerValue = header.value ? header.value.trim() : "";
                            if (headerKey && headerValue) {
                                if (!/^X-/i.test(headerKey)) {
                                    sipHeaderError = `Custom SIP header anahtarları "X-" ile başlamalıdır: ${headerKey}`;
                                } else {
                                    sipHeaders[headerKey] = headerValue;
                                }
                            }
                        });
                        if (sipHeaderError) {
                            this.$message.error(sipHeaderError);
                            this.transferCallSubmitting = false;
                            return;
                        }
                        if (Object.keys(sipHeaders).length > 0) {
                            toolData.custom_sip_headers = sipHeaders;
                        } else {
                            delete toolData.custom_sip_headers;
                        }

                        // Düzenleme mi ekleme mi kontrol et
                        if (this.isEditing && this.editingTool) {
                            // Eski adını koru (editingTool'dan)
                            await this.updateToolInLLM(this.editingTool.name, toolData);
                        } else {
                            await this.saveToolToLLM(toolData);
                        }

                        this.closeTransferCallModal();
                    } catch (error) {
                        console.error("Transfer Call fonksiyonu kaydedilirken hata:", error);
                        this.$message.error("Fonksiyon kaydedilirken bir hata oluştu");
                    } finally {
                        this.transferCallSubmitting = false;
                    }
                }
            });
        },
        addTransferCallHeader() {
            this.transferCallHeaders.push({ key: "", value: "" });
        },
        removeTransferCallHeader(index) {
            this.transferCallHeaders.splice(index, 1);
        },
        loadTransferCallHeaders(tool) {
            if (tool && tool.custom_sip_headers && typeof tool.custom_sip_headers === "object") {
                this.transferCallHeaders = Object.entries(tool.custom_sip_headers).map(([key, value]) => ({
                    key,
                    value,
                }));
            } else {
                this.transferCallHeaders = [];
            }
        },

        onTransferTypeChange(e) {
            const value = e.target.value;
            this.transferCallForm.setFieldsValue({
                destination_type: value,
            });
            if (value === "inferred") {
                this.transferCallForm.setFieldsValue({
                    has_extension: false,
                    extension: "",
                });
            }
        },

        onWarmTransferChange(e) {
            const value = e.target.value;
            this.transferCallForm.setFieldsValue({
                transfer_type: value,
            });
        },

        // Press Digit Form Methods
        closePressDigitModal() {
            this.showPressDigitModal = false;
            this.pressDigitForm.resetFields();
            this.isEditing = false;
            this.editingTool = null;
        },
        handlePressDigitSubmit() {
            this.pressDigitForm.validateFields(async (err, values) => {
                if (!err) {
                    try {
                        this.pressDigitSubmitting = true;

                        const toolData = {
                            type: "press_digit",
                            name: values.name,
                            description: values.description,
                            pause_detection_delay: values.pause_detection_delay,
                        };

                        // Düzenleme mi ekleme mi kontrol et
                        if (this.isEditing && this.editingTool) {
                            // Eski adını koru (editingTool'dan)
                            await this.updateToolInLLM(this.editingTool.name, toolData);
                        } else {
                            await this.saveToolToLLM(toolData);
                        }

                        this.closePressDigitModal();
                    } catch (error) {
                        console.error("Press Digit fonksiyonu kaydedilirken hata:", error);
                        this.$message.error("Fonksiyon kaydedilirken bir hata oluştu");
                    } finally {
                        this.pressDigitSubmitting = false;
                    }
                }
            });
        },

        // Custom Function Form Methods
        closeCustomFunctionModal() {
            this.showCustomFunctionModal = false;
            this.customFunctionForm.resetFields();
            this.isEditing = false;
            this.editingTool = null;
            // Key-value editor'ları temizle
            this.customFunctionHeaders = [];
            this.customFunctionQueryParams = [];
            this.customFunctionResponseVariables = [];
            this.selectedAutomation = null;
        },

        // Headers methods
        addHeader() {
            this.customFunctionHeaders.push({ key: '', value: '' });
        },
        removeHeader(index) {
            this.customFunctionHeaders.splice(index, 1);
        },

        // Query params methods
        addQueryParam() {
            this.customFunctionQueryParams.push({ key: '', value: '' });
        },
        removeQueryParam(index) {
            this.customFunctionQueryParams.splice(index, 1);
        },

        // Response variables methods
        addResponseVariable() {
            this.customFunctionResponseVariables.push({ key: '', value: '' });
        },
        removeResponseVariable(index) {
            this.customFunctionResponseVariables.splice(index, 1);
        },

        // Key-value editor'ları yükle
        loadKeyValueEditors(tool) {
            // Headers
            if (tool.headers && typeof tool.headers === 'object') {
                this.customFunctionHeaders = Object.entries(tool.headers).map(([key, value]) => ({ key, value }));
            } else {
                this.customFunctionHeaders = [];
            }

            // Query params
            if (tool.query_params && typeof tool.query_params === 'object') {
                this.customFunctionQueryParams = Object.entries(tool.query_params).map(([key, value]) => ({ key, value }));
            } else {
                this.customFunctionQueryParams = [];
            }

            // Response variables
            if (tool.response_variables && typeof tool.response_variables === 'object') {
                this.customFunctionResponseVariables = Object.entries(tool.response_variables).map(([key, value]) => ({ key, value }));
            } else {
                this.customFunctionResponseVariables = [];
            }

            // URL'den otomasyon bilgisini çıkar
            this.extractAutomationFromUrl(tool.url);
        },

        // URL'den otomasyon bilgisini çıkar
        extractAutomationFromUrl(url) {
            if (!url) {
                this.selectedAutomation = null;
                return;
            }

            // Webhook automation URL pattern: /webhook/automation/{siteId}/{webhookId}
            const webhookPattern = /\/webhook\/automation\/([^\/]+)\/([^\/]+)/;
            const match = url.match(webhookPattern);

            if (match) {
                const [, siteId, webhookId] = match;

                // Automation listesinden eşleşeni bul
                const automation = this.webhookAutomations.find(a =>
                    a.siteId === siteId && a.webhookId === webhookId
                );

                if (automation) {
                    this.selectedAutomation = automation;
                } else {
                    // Otomasyon bulunamazsa sadece URL bilgisini göster
                    this.selectedAutomation = {
                        id: webhookId,
                        name: `Webhook (${webhookId.substring(0, 8)}...)`,
                        siteId: siteId,
                        webhookId: webhookId
                    };
                }
            } else {
                this.selectedAutomation = null;
            }
        },

        // Otomasyon detayına git
        goToAutomationDetail(automationId) {
            this.$router.push(`/automations/${automationId}/design`);
        },
        validateParameters(rule, value, callback) {
            if (!value) {
                callback("Parametreler gereklidir!");
                return;
            }

            try {
                const parsedValue = JSON.parse(value);
                if (typeof parsedValue !== "object" || parsedValue === null || Array.isArray(parsedValue)) {
                    callback("Geçerli bir JSON Schema objesi girilmelidir!");
                    return;
                }

                if (parsedValue.type !== "object") {
                    callback('Root type "object" olmalıdır!');
                    return;
                }

                callback();
            } catch (error) {
                callback("Geçerli bir JSON formatı değil!");
            }
        },
        applySampleParameters(index) {
            if (index < 1 || index > this.sampleParameters.length) {
                return;
            }

            const sample = this.sampleParameters[index - 1];
            this.customFunctionForm.setFieldsValue({
                parameters: JSON.stringify(sample, null, 2),
            });
        },
        handleCustomFunctionSubmit() {
            this.customFunctionForm.validateFields(async (err, values) => {
                if (!err) {
                    try {
                        this.customFunctionSubmitting = true;

                        // JSON parametrelerini parse et
                        const parameters = JSON.parse(values.parameters);

                        const toolData = {
                            type: "custom",
                            name: values.name,
                            description: values.description,
                            method: values.method || 'POST',
                            url: values.url,
                            speak_during_execution: values.speak_during_execution,
                            speak_after_execution: values.speak_after_execution,
                            parameters: parameters,
                        };

                        // Headers objesi oluştur
                        const headers = {};
                        this.customFunctionHeaders.forEach(header => {
                            if (header.key && header.value) {
                                headers[header.key] = header.value;
                            }
                        });
                        if (Object.keys(headers).length > 0) {
                            toolData.headers = headers;
                        }

                        // Query params objesi oluştur
                        const queryParams = {};
                        this.customFunctionQueryParams.forEach(param => {
                            if (param.key && param.value) {
                                queryParams[param.key] = param.value;
                            }
                        });
                        if (Object.keys(queryParams).length > 0) {
                            toolData.query_params = queryParams;
                        }

                        // Response variables objesi oluştur
                        const responseVariables = {};
                        this.customFunctionResponseVariables.forEach(variable => {
                            if (variable.key && variable.value) {
                                responseVariables[variable.key] = variable.value;
                            }
                        });
                        if (Object.keys(responseVariables).length > 0) {
                            toolData.response_variables = responseVariables;
                        }

                        // Opsiyonel alanları ekle
                        if (values.execution_message_description) {
                            toolData.execution_message_description = values.execution_message_description;
                        }

                        if (values.timeout_ms) {
                            toolData.timeout_ms = values.timeout_ms;
                        }

                        // Düzenleme mi ekleme mi kontrol et
                        if (this.isEditing && this.editingTool) {
                            // Eski adını koru (editingTool'dan)
                            await this.updateToolInLLM(this.editingTool.name, toolData);
                        } else {
                            await this.saveToolToLLM(toolData);
                        }

                        this.closeCustomFunctionModal();
                    } catch (error) {
                        console.error("Custom Function kaydedilirken hata:", error);
                        this.$message.error("Fonksiyon kaydedilirken bir hata oluştu");
                    } finally {
                        this.customFunctionSubmitting = false;
                    }
                }
            });
        },

        // Tool kaydetme ortak metodu
        async saveToolToLLM(toolData) {
            if (!this.agent || !this.agent.llm || !this.agent.response_engine || !this.agent.response_engine.llm_id) {
                throw new Error("Agent bilgisi bulunamadı!");
            }

            // Mevcut tools listesini al
            const generalTools = Array.isArray(this.agent.llm.general_tools) ? [...this.agent.llm.general_tools] : [];

            // Tool name'i kontrol et, aynı isimde varsa güncelle yoksa ekle
            const existingToolIndex = generalTools.findIndex((t) => t.name === toolData.name);

            if (existingToolIndex >= 0) {
                // Mevcut tool'u güncelle
                generalTools[existingToolIndex] = toolData;
            } else {
                // Yeni tool ekle
                generalTools.push(toolData);
            }

            // LLM güncelleme verisi
            const llmData = {
                general_tools: generalTools,
            };

            // LLM güncelleme API çağrısı
            const updatedLLM = await VOICE_AGENT_API.llms.update(this.agent.response_engine.llm_id, llmData);

            // UI ve kullanıcıya bildirim
            this.$message.success("Fonksiyon başarıyla kaydedildi");

            // Fonksiyonları yeniden yükle - önce agent.llm'i güncelle
            if (this.agent && this.agent.llm) {
                this.agent.llm = { ...updatedLLM };
            }

            // Önce arabirimin güncellenmesini sağla, sonra fonksiyonları yükle
            this.$nextTick(() => {
                this.loadTools();
            });

            // Üst bileşene bildir (AgentEditor üzerinden agent güncellemesi için)
            this.$emit("llm-updated", updatedLLM);

            return updatedLLM;
        },
        showWebhookAutomationSelector() {
            this.showWebhookAutomationSelectorModal = true;
            this.fetchWebhookAutomations();
        },
        closeWebhookAutomationSelectorModal() {
            this.showWebhookAutomationSelectorModal = false;
        },
        async fetchWebhookAutomations() {
            try {
                this.webhookAutomationLoading = true;

                // Store'daki otomasyon verilerini güncelle
                await this.$store.dispatch("automation/syncAutomations");
            } catch (error) {
                console.error("Webhook otomasyonları yüklenirken hata:", error);
                this.$message.error("Webhook otomasyonları yüklenirken bir hata oluştu");
            } finally {
                this.webhookAutomationLoading = false;
            }
        },
        async selectWebhookAutomation(automation) {
            try {
                // Webhook URL'sini oluştur
                const webhookUrl = `${CONFIG.SOCKET_URL}/webhook/automation/${automation.siteId}/${automation.webhookId}`;

                // Form alanını güncelle
                this.customFunctionForm.setFieldsValue({
                    url: webhookUrl,
                });

                // Seçilen otomasyonu kaydet
                this.selectedAutomation = automation;

                this.closeWebhookAutomationSelectorModal();
                this.$message.success("Webhook URL'si başarıyla kopyalandı");
            } catch (error) {
                console.error("Webhook otomasyon seçilirken hata:", error);
                this.$message.error("Webhook otomasyon seçilirken bir hata oluştu");
            }
        },
        getWebhookAutomationDate(date) {
            return moment(date).format("DD.MM.YYYY HH:mm");
        },
        // Webhook otomasyonları sayfasına yönlendir
        goToAutomations() {
            this.$router.push("/automations");
            this.closeWebhookAutomationSelectorModal();
        },

        // Extract Dynamic Variable Form Methods
        closeExtractDynamicVariableModal() {
            this.showExtractDynamicVariableModal = false;
            this.extractDynamicVariableForm.resetFields();
            this.extractDynamicVariables = [];
            this.isEditing = false;
            this.editingTool = null;
        },
        addExtractVariable() {
            this.extractDynamicVariables.push({
                type: 'string',
                name: 'customer_name',
                description: 'The name of the customer.',
                examplesText: 'John Doe, Jane Smith'
            });
        },
        removeExtractVariable(index) {
            this.extractDynamicVariables.splice(index, 1);
        },
        handleExtractDynamicVariableSubmit() {
            this.extractDynamicVariableForm.validateFields(async (err, values) => {
                if (!err) {
                    try {
                        this.extractDynamicVariableSubmitting = true;

                        // Variables dizisini oluştur
                        const variables = this.extractDynamicVariables.map(v => ({
                            type: v.type,
                            name: v.name,
                            description: v.description,
                            examples: v.examplesText ? v.examplesText.split(',').map(ex => ex.trim()).filter(ex => ex) : []
                        }));

                        const toolData = {
                            type: 'extract_dynamic_variable',
                            name: values.name,
                            description: values.description,
                            variables: variables
                        };

                        // Düzenleme mi ekleme mi kontrol et
                        if (this.isEditing && this.editingTool) {
                            await this.updateToolInLLM(this.editingTool.name, toolData);
                        } else {
                            await this.saveToolToLLM(toolData);
                        }

                        this.closeExtractDynamicVariableModal();
                    } catch (error) {
                        console.error('Extract Dynamic Variable fonksiyonu kaydedilirken hata:', error);
                        this.$message.error('Fonksiyon kaydedilirken bir hata oluştu');
                    } finally {
                        this.extractDynamicVariableSubmitting = false;
                    }
                }
            });
        },

        // Agent Swap Form Methods
        closeAgentSwapModal() {
            this.showAgentSwapModal = false;
            this.agentSwapForm.resetFields();
            this.availableAgents = [];
            this.isEditing = false;
            this.editingTool = null;
        },
        async fetchAvailableAgents() {
            try {
                const agents = await VOICE_AGENT_API.agents.list(true);
                // Mevcut agent'ı filtrele
                this.availableAgents = agents.filter(ag => ag.agent_id !== this.agent.agent_id);
            } catch (error) {
                console.error('Ajanlar yüklenirken hata:', error);
                this.$message.error('Ajanlar yüklenirken bir hata oluştu');
            }
        },
        filterAgentOption(input, option) {
            return (
                option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
            );
        },
        handleAgentSwapSubmit() {
            this.agentSwapForm.validateFields(async (err, values) => {
                if (!err) {
                    try {
                        this.agentSwapSubmitting = true;

                        const toolData = {
                            type: 'agent_swap',
                            name: values.name,
                            description: values.description,
                            agent_id: values.agent_id,
                            post_call_analysis_setting: values.post_call_analysis_setting,
                            webhook_setting: values.webhook_setting,
                            speak_during_execution: values.speak_during_execution
                        };

                        // Opsiyonel alanları ekle
                        if (values.execution_message_description) {
                            toolData.execution_message_description = values.execution_message_description;
                        }

                        // Düzenleme mi ekleme mi kontrol et
                        if (this.isEditing && this.editingTool) {
                            await this.updateToolInLLM(this.editingTool.name, toolData);
                        } else {
                            await this.saveToolToLLM(toolData);
                        }

                        this.closeAgentSwapModal();
                    } catch (error) {
                        console.error('Agent Transfer fonksiyonu kaydedilirken hata:', error);
                        this.$message.error('Fonksiyon kaydedilirken bir hata oluştu');
                    } finally {
                        this.agentSwapSubmitting = false;
                    }
                }
            });
        },

        // Tool güncelleme metodu
        async updateToolInLLM(oldToolName, toolData) {
            if (!this.agent || !this.agent.llm || !this.agent.response_engine || !this.agent.response_engine.llm_id) {
                throw new Error("Agent bilgisi bulunamadı!");
            }

            // Mevcut tools listesini al
            const generalTools = Array.isArray(this.agent.llm.general_tools) ? [...this.agent.llm.general_tools] : [];

            // Tool name'i kontrol et, eski adı bul ve güncelle
            const existingToolIndex = generalTools.findIndex((t) => t.name === oldToolName);

            if (existingToolIndex >= 0) {
                // Mevcut tool'u güncelle
                generalTools[existingToolIndex] = toolData;
            } else {
                // Eğer bulunamazsa yeni ekle (asla olmamalı ama güvenlik için)
                generalTools.push(toolData);
            }

            // LLM güncelleme verisi
            const llmData = {
                general_tools: generalTools,
            };

            // LLM güncelleme API çağrısı
            const updatedLLM = await VOICE_AGENT_API.llms.update(this.agent.response_engine.llm_id, llmData);

            // UI ve kullanıcıya bildirim
            this.$message.success("Fonksiyon başarıyla güncellendi");

            // Fonksiyonları yeniden yükle - önce agent.llm'i güncelle
            if (this.agent && this.agent.llm) {
                this.agent.llm = { ...updatedLLM };
            }

            // Önce arabirimin güncellenmesini sağla, sonra fonksiyonları yükle
            this.$nextTick(() => {
                this.loadTools();
            });

            // Üst bileşene bildir (AgentEditor üzerinden agent güncellemesi için)
            this.$emit("llm-updated", updatedLLM);

            return updatedLLM;
        },
    },
};
</script>

<style lang="scss">
.functions-tab {
    padding: 20px 0;

    .tab-header {
        margin-bottom: 20px;

        h3 {
            margin: 0 0 10px 0;
            font-weight: 500;
        }

        p {
            color: #666;
            margin: 0;
        }
    }

    .functions-section {
        background-color: #f9f9f9;
        padding: 15px;
        border-radius: 4px;
        margin-bottom: 20px;
        border: 1px solid #e8e8e8;

        .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 15px;

            h4 {
                margin: 0;
                font-weight: 500;
            }
        }
    }

    .empty-state {
        padding: 30px 0;
        text-align: center;
    }

    .actions {
        display: flex;
        gap: 5px;
        justify-content: flex-end;
    }

    .spin {
        animation: spin 1.5s infinite linear;
    }

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
}

/* Modal içindeki seçiciler */
.ant-modal-root {
    .tool-type-selection {
        .tool-type-list {
            display: flex;
            flex-direction: column;
            gap: 10px;

            .tool-type-option {
                display: flex;
                align-items: flex-start;
                padding: 10px;
                border: 1px solid #e8e8e8;
                border-radius: 4px;
                cursor: pointer;

                &:hover {
                    background-color: #f5f5f5;
                }

                .tool-icon {
                    margin-right: 10px;

                    .material-icons {
                        font-size: 24px;
                        color: #1890ff;
                    }
                }

                .tool-info {
                    flex: 1;

                    .tool-name {
                        font-weight: 500;
                        margin-bottom: 5px;
                    }

                    .tool-description {
                        color: #666;
                        font-size: 12px;
                    }
                }
            }
        }
    }

    .modal-footer {
        margin-top: 20px;
        text-align: right;

        .supsis-button {
            margin-left: 8px;
        }
    }

    .hint-text {
        color: #666;
        font-size: 12px;
        margin-top: 5px;
    }

    .parameters-toolbar {
        margin-bottom: 10px;

        .sample-buttons {
            display: flex;
            gap: 5px;
        }
    }

    /* Form elemanlarının stillerini düzelt */
    .ant-form-item-label {
        label {
            font-weight: 500;
        }
    }

    .ant-checkbox-wrapper {
        margin-bottom: 8px;
    }

    .ant-input-number {
        width: 100%;
    }

    /* Custom Function form stilleri */
    .ant-form-item {
        margin-bottom: 16px;
    }

    /* Radio buton ile text hizalama */
    .ant-radio-wrapper {
        .ant-radio {
            top: 0;
            margin-right: 8px;
        }
    }
}

.url-input-group {
    display: flex;
    gap: 10px;
    align-items: center;

    .ant-select {
        flex-shrink: 0;
    }

    .ant-input {
        flex: 1;
    }

    .supsis-button {
        white-space: nowrap;
        flex-shrink: 0;
    }
}

.key-value-editor {
    .key-value-row {
        display: flex;
        gap: 8px;
        margin-bottom: 8px;
        align-items: center;

        .ant-input {
            flex-shrink: 0;
        }

        .ant-btn {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .ant-btn-dashed {
        margin-top: 8px;

        .material-icons {
            font-size: 16px;
            margin-right: 4px;
        }
    }
}

.selected-automation {
    margin-top: 8px;
    padding: 8px 12px;
    background-color: #f0f9ff;
    border: 1px solid #bae7ff;
    border-radius: 4px;

    .automation-info {
        display: flex;
        align-items: center;
        gap: 8px;

        .material-icons {
            font-size: 16px;
            color: #1890ff;
        }

        span {
            font-size: 13px;
            color: #333;
        }

        .ant-btn-link {
            padding: 0;
            height: auto;
            font-size: 12px;

            .material-icons {
                font-size: 14px;
            }
        }
    }
}

.webhook-automation-list {
    max-height: 400px;
    overflow-y: auto;

    .ant-table-wrapper {
        margin-bottom: 16px;
    }
}

.no-webhooks {
    padding: 20px;
    text-align: center;
}

.webhook-header {
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .search-input {
        width: 240px;
    }
}

/* Modal içindeki wrapper'lar için global stiller */
#end-call-modal-wrapper {
    padding: 0 5px;

    .ant-form-item {
        margin-bottom: 18px;

        .ant-form-item-label {
            label {
                font-weight: 500;
                color: #333;
            }
        }
    }

    .hint-text {
        color: #666;
        font-size: 12px;
        font-style: italic;
    }

    .modal-footer {
        display: flex;
        justify-content: flex-end;
        margin-top: 24px;

        .supsis-button {
            margin-left: 10px;
        }
    }
}

#transfer-call-modal-wrapper {
    padding: 0 5px;

    .ant-form-item {
        margin-bottom: 18px;

        .ant-form-item-label {
            label {
                font-weight: 500;
                color: #333;
            }
        }
    }

    .ant-radio-wrapper {
        margin-right: 15px;

        .ant-radio + span {
            color: #333;
            font-weight: 500;
        }
    }

    .hint-text {
        color: #666;
        font-size: 12px;
        font-style: italic;
        margin-top: 4px;
    }

    .transfer-destination-options {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
    }

    .transfer-extension-row {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-top: 10px;
        flex-wrap: wrap;
    }

    .modal-footer {
        display: flex;
        justify-content: flex-end;
        margin-top: 24px;

        .supsis-button {
            margin-left: 10px;
        }
    }

    .ant-textarea {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    }
}

#press-digit-modal-wrapper {
    padding: 0 5px;

    .ant-form-item {
        margin-bottom: 18px;

        .ant-form-item-label {
            label {
                font-weight: 500;
                color: #333;
            }
        }
    }

    .hint-text {
        color: #666;
        font-size: 12px;
        font-style: italic;
    }

    .modal-footer {
        display: flex;
        justify-content: flex-end;
        margin-top: 24px;

        .supsis-button {
            margin-left: 10px;
        }
    }
}

#custom-function-modal-wrapper {
    padding: 0 5px;

    .ant-form-item {
        margin-bottom: 18px;

        .ant-form-item-label {
            label {
                font-weight: 500;
                color: #333;
            }
        }
    }

    .hint-text {
        color: #666;
        font-size: 12px;
        font-style: italic;
    }

    .parameters-toolbar {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 8px;

        .sample-buttons {
            display: flex;
            gap: 8px;

            .ant-btn {
                font-size: 12px;

                &:hover {
                    color: #1890ff;
                    border-color: #1890ff;
                }
            }
        }
    }

    .url-input-group {
        display: flex;
        gap: 8px;

        .ant-input {
            flex: 1;
        }
    }

    .modal-footer {
        display: flex;
        justify-content: flex-end;
        margin-top: 24px;

        .supsis-button {
            margin-left: 10px;
        }
    }
}

#extract-dynamic-variable-modal-wrapper {
    padding: 0 5px;

    .ant-form-item {
        margin-bottom: 18px;

        .ant-form-item-label {
            label {
                font-weight: 500;
                color: #333;
            }
        }
    }

    .hint-text {
        color: #666;
        font-size: 12px;
        font-style: italic;
    }

    .variables-list {
        .variable-item {
            background-color: #fafafa;
            border: 1px solid #e8e8e8;
            border-radius: 4px;
            padding: 16px;
            margin-bottom: 16px;

            .variable-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 12px;

                h5 {
                    margin: 0;
                    font-weight: 500;
                    color: #333;
                }
            }

            .ant-form-item {
                margin-bottom: 12px;

                &:last-child {
                    margin-bottom: 0;
                }
            }
        }

        .ant-btn-dashed {
            margin-top: 8px;

            .material-icons {
                font-size: 16px;
                margin-right: 4px;
            }
        }
    }

    .modal-footer {
        display: flex;
        justify-content: flex-end;
        margin-top: 24px;

        .supsis-button {
            margin-left: 10px;
        }
    }
}

#agent-swap-modal-wrapper {
    padding: 0 5px;

    .ant-form-item {
        margin-bottom: 18px;

        .ant-form-item-label {
            label {
                font-weight: 500;
                color: #333;
            }
        }
    }

    .ant-radio-wrapper {
        margin-right: 15px;

        .ant-radio + span {
            color: #333;
            font-weight: 500;
        }
    }

    .hint-text {
        color: #666;
        font-size: 12px;
        font-style: italic;
        margin-top: 4px;
    }

    .modal-footer {
        display: flex;
        justify-content: flex-end;
        margin-top: 24px;

        .supsis-button {
            margin-left: 10px;
        }
    }

    .ant-textarea {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    }
}
</style>
